import { z } from 'zod';
import { optionalBlankText } from './business-card';
import { BusinessCardTagsDto, BusinessCardTagsPatch, EMPTY_BUSINESS_CARD_TAGS } from './business-card-tags';
import { InterestSlug } from './interests';
import { PublicPersonalityCodeDto } from './personality-code';

/**
 * «Кого ищу» — ОДИН словарь на оба режима знакомств (личный и деловой). Два отдельных
 * enum'а потребовали бы миграции данных: в визитке у людей уже выбраны и личные
 * (`friends`, `dates`), и деловые (`collaborators`, `clients`) значения, выбранные до
 * появления режимов. Какие слаги показывать в пикере конкретного режима — говорит
 * `LOOKING_FOR_BY_MODE` (`networking.ts`), и эта карта применяется только НА ЗАПИСЬ:
 * чтение отдаёт что лежит, иначе чужой для режима слаг молча терялся бы.
 */
export const LookingForSlug = z.enum([
  'friends',
  'dates',
  'collaborators',
  'travel',
  'clients',
  // Деловые: то, что прежним словарём выразить было нечем.
  'investment',
  'team',
  'mentorship',
]);
export type LookingForSlug = z.infer<typeof LookingForSlug>;

/** «Что даю» — тот же принцип: один словарь, видимость по режиму в `OFFERING_BY_MODE`. */
export const OfferingSlug = z.enum(['show_city', 'host', 'teach', 'work_together', 'invest']);
export type OfferingSlug = z.infer<typeof OfferingSlug>;

export const SOCIAL_NETWORKS = [
  'telegram',
  'instagram',
  'whatsapp',
  'youtube',
  'tiktok',
  'linkedin',
  'facebook',
  'x',
] as const;
export const SocialNetwork = z.enum(SOCIAL_NETWORKS);
export type SocialNetwork = z.infer<typeof SocialNetwork>;

/** Ник без решётки. WhatsApp — только цифры номера. Иначе null. */
export function normalizeSocialHandle(network: SocialNetwork, raw: string): string | null {
  const stripped = raw.trim().replace(/^@+/u, '').trim();
  if (network === 'whatsapp') {
    const digits = stripped.replace(/\D/g, '');
    return digits.length >= 8 && digits.length <= 15 ? digits : null;
  }
  if (!/^[A-Za-z0-9._-]{1,64}$/.test(stripped)) return null;
  return stripped;
}

export const PublicCardSocialDto = z
  .object({
    network: SocialNetwork,
    handle: z.string().trim().max(64),
  })
  .superRefine((row, ctx) => {
    if (!normalizeSocialHandle(row.network, row.handle)) {
      ctx.addIssue({ code: 'custom', path: ['handle'], message: 'bad handle' });
    }
  })
  .transform((row) => ({
    network: row.network,
    handle: normalizeSocialHandle(row.network, row.handle) ?? '',
  }));
export type PublicCardSocialDto = z.infer<typeof PublicCardSocialDto>;

export const PublicCardSocialsDto = z
  .array(PublicCardSocialDto)
  .max(SOCIAL_NETWORKS.length)
  .superRefine((items, ctx) => {
    const seen = new Set<string>();
    for (const [index, item] of items.entries()) {
      if (seen.has(item.network)) {
        ctx.addIssue({ code: 'custom', path: [index, 'network'], message: 'duplicate network' });
      }
      seen.add(item.network);
    }
  });
export type PublicCardSocialsDto = z.infer<typeof PublicCardSocialsDto>;

/**
 * Соцсети из JSON-колонки (`NetworkingProfile.socials`, `Club.socials`). Строки,
 * которые контракт уже не принимает, и повтор сети отбрасываются, а не роняют чтение.
 */
export function parseStoredSocials(raw: unknown): PublicCardSocialDto[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const socials: PublicCardSocialDto[] = [];
  for (const item of raw) {
    const parsed = PublicCardSocialDto.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.network)) continue;
    seen.add(parsed.data.network);
    socials.push(parsed.data);
  }
  return socials;
}

/** Спорт, мероприятия или знакомства на визитке. Знакомства — только с включённым тумблером. */
export const PublicCardDirectionDto = z.object({
  kind: z.enum(['sport', 'events', 'dating']),
  about: z.string(),
  tags: z.array(z.string()),
});
export type PublicCardDirectionDto = z.infer<typeof PublicCardDirectionDto>;

export const PublicCardExternalLinkDto = z.object({
  label: z.string().trim().min(1).max(40),
  /**
   * Только https (спека 2026-09-03). `z.url()` без ограничения схемы пропускает
   * `javascript:` и `data:` — а ссылка попадает в `href` серверного HTML визитки,
   * то есть это была бы stored XSS на публичной странице, а не косметика.
   */
  url: z.url({ protocol: /^https$/ }),
});
export type PublicCardExternalLinkDto = z.infer<typeof PublicCardExternalLinkDto>;

const Iso6391 = z.string().regex(/^[a-z]{2}$/);

/** Снимок витрины на NetworkingProfile — и GET /me.publicCard, и публичная страница. */
export const PublicCardProfileFields = z.object({
  headline: z.string().nullable(),
  superpower: z.string().nullable(),
  lookingFor: z.array(LookingForSlug).max(3),
  offering: z.array(OfferingSlug).max(3),
  spokenLanguages: z.array(Iso6391).max(8),
  showUpcomingRsvps: z.boolean(),
  links: z.array(PublicCardExternalLinkDto).max(3),
  socials: PublicCardSocialsDto,
  /** Сырые колонки бизнес-карты. Публичная страница подставляет запасной текст сама. */
  bioRole: z.string().nullable().optional(),
  bioSkills: z.string().nullable().optional(),
  bioIndustry: z.string().nullable().optional(),
  bioRequest: z.string().nullable().optional(),
  ...BusinessCardTagsDto.shape,
});
export type PublicCardProfileFields = z.infer<typeof PublicCardProfileFields>;

export const EMPTY_PUBLIC_CARD_PROFILE: PublicCardProfileFields = {
  headline: null,
  superpower: null,
  lookingFor: [],
  offering: [],
  spokenLanguages: [],
  showUpcomingRsvps: false,
  links: [],
  socials: [],
  bioRole: null,
  bioSkills: null,
  bioIndustry: null,
  bioRequest: null,
  ...EMPTY_BUSINESS_CARD_TAGS,
};

/** Тема пластика визитки. Неизвестное значение сервисы читают как `ours`. */
export const PublicCardTheme = z.enum([
  'ours',
  'gold',
  'blue',
  'red',
  'orange',
  'green',
  'purple',
  'rose',
  'coral',
  'teal',
  'lavender',
  'cobalt',
  'magenta',
  'copper',
  'olive',
  'ice',
]);
export type PublicCardTheme = z.infer<typeof PublicCardTheme>;

export const MePublicCardDto = z.object({
  enabled: z.boolean(),
  /** Свой логотип шапки. null — на визитке логотип UpMe. */
  logoUrl: z.string().nullable(),
  /** Тема пластика. Старый ответ без поля читается как «Наша». */
  theme: PublicCardTheme.default('ours'),
  url: z.string().nullable(),
  /** Занятый короткий код на корне апекса. null — человек его не задавал. */
  alias: z.string().nullable(),
  /**
   * Самая короткая длина, которую текущий статус может занять.
   * null — короткого адреса нет (статуса нет).
   */
  aliasMinLength: z.number().int().min(2).max(16).nullable(),
  ...PublicCardProfileFields.shape,
});
export type MePublicCardDto = z.infer<typeof MePublicCardDto>;

export const EMPTY_ME_PUBLIC_CARD: MePublicCardDto = {
  enabled: false,
  logoUrl: null,
  theme: 'ours',
  url: null,
  alias: null,
  aliasMinLength: null,
  ...EMPTY_PUBLIC_CARD_PROFILE,
};

/** Тело PATCH /me/public-alias. `null` снимает код, строка — занимает. */
export const UpdatePublicAliasInput = z.object({
  alias: z.string().max(16).nullable(),
});
export type UpdatePublicAliasInput = z.infer<typeof UpdatePublicAliasInput>;

/** Тело PATCH /me/public-card: снимок витрины и/или включение. `enabled: false`
 *  сервис отвергает 422 `anonymity_unavailable` — каталог открыт для всех, а
 *  анонимность запланирована как платная функция. Хотя бы одно поле. */
export const UpdatePublicCardInput = z
  .object({
    enabled: z.boolean().optional(),
    headline: z.string().trim().max(80).nullable().optional(),
    bioRole: optionalBlankText(100),
    superpower: z.string().trim().max(140).nullable().optional(),
    lookingFor: z.array(LookingForSlug).max(3).optional(),
    offering: z.array(OfferingSlug).max(3).optional(),
    spokenLanguages: z.array(Iso6391).max(8).optional(),
    showUpcomingRsvps: z.boolean().optional(),
    links: z.array(PublicCardExternalLinkDto).max(3).optional(),
    socials: PublicCardSocialsDto.optional(),
    theme: PublicCardTheme.optional(),
    ...BusinessCardTagsPatch.shape,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });
export type UpdatePublicCardInput = z.infer<typeof UpdatePublicCardInput>;

/**
 * Ближайшее событие на визитке. `priceLabel` — уже собранная строка: цену
 * форматирует formatPrice из @upme/view-logic (валюта города события, донаты,
 * «Бесплатно» при null/0), и ему нужны City.currency и сырая локаль, которых у
 * шаблона нет. Единственное исключение из правила «API не отдаёт готовые
 * строки»; альтернатива — тащить price/currency/isDonation и дублировать
 * логику форматирования.
 */
export const PublicCardEventDto = z.object({
  id: z.uuid(),
  title: z.string(),
  url: z.string().nullable(),
  startsAt: z.iso.datetime().nullable(),
  /** IANA-зона города СОБЫТИЯ (не владельца визитки): цена берётся оттуда же. */
  timeZone: z.string().nullable(),
  coverUrl: z.string().nullable(),
  priceLabel: z.string(),
  /** EventCategory.name как есть: nameEn в модели нет (см. «Известное ограничение» спеки). */
  categoryName: z.string().nullable(),
  categoryIcon: z.string().nullable(),
  attendeeCount: z.number().int().nonnegative(),
});
export type PublicCardEventDto = z.infer<typeof PublicCardEventDto>;

/** Формат оказания услуги — зеркало Prisma-энума ServiceFormat. */
/**
 * Клуб на визитке — только тот, которым человек ВЛАДЕЕТ: визитка говорит «я
 * веду такой-то клуб», а не перечисляет, где он состоит. Членство в чужом
 * клубе на анонимной странице не показывается вовсе.
 */
export const PublicCardClubDto = z.object({
  id: z.uuid(),
  name: z.string(),
  url: z.string().nullable(),
  logoUrl: z.string().nullable(),
  memberCount: z.number().int().nonnegative(),
});
export type PublicCardClubDto = z.infer<typeof PublicCardClubDto>;

export const PublicCardServiceFormat = z.enum(['AT_PROVIDER', 'AT_CLIENT', 'ONLINE']);
export type PublicCardServiceFormat = z.infer<typeof PublicCardServiceFormat>;

/**
 * Услуга на визитке. `priceText` отдаётся строкой как есть: в Service.priceText
 * человек пишет и валюту, и модификатор («от 900 ₾»), парсить нельзя.
 */
export const PublicCardServiceDto = z.object({
  id: z.uuid(),
  title: z.string(),
  url: z.string().nullable(),
  priceText: z.string().nullable(),
  /** Свободный текст владельца; null → на странице «незаполнено». */
  description: z.string().nullable(),
  format: PublicCardServiceFormat,
  coverUrl: z.string().nullable(),
});
export type PublicCardServiceDto = z.infer<typeof PublicCardServiceDto>;

/**
 * Три числа оборота Connection Card. Единственное определение на всю систему:
 * его же отдают `/me/home` владельцу и публичная визитка гостю — иначе под
 * одной подписью «Друзья» человек видит у себя одно число, а гость другое.
 */
export const ConnectionCardStatsDto = z.object({
  /** Прошедшие CONFIRMED EVENT|MEETING. */
  meetings: z.number().int().nonnegative(),
  /** Уникальные люди: мэтчи ∪ DIRECT. Соцграф (`Person`) сюда не входит. */
  friends: z.number().int().nonnegative(),
  /** Сколько людей привёл: `User.referredById = owner`. */
  referrals: z.number().int().nonnegative(),
});
export type ConnectionCardStatsDto = z.infer<typeof ConnectionCardStatsDto>;

/**
 * Метрики. Всегда на странице (ADR-070): ноль — факт, не повод прятать блок.
 */
export const PublicCardStatsDto = ConnectionCardStatsDto.extend({
  followers: z.number().int().nonnegative(),
  /** Все публичные события организатора, не только предстоящие — суммарный опыт. */
  events: z.number().int().nonnegative(),
});
export type PublicCardStatsDto = z.infer<typeof PublicCardStatsDto>;

/** Оборот Connection Card. Не кладём в колоду знакомств обязательными полями. */
export const ConnectionCardBackDto = ConnectionCardStatsDto.extend({
  offering: z.array(OfferingSlug),
  services: z.array(z.object({ id: z.uuid(), title: z.string().min(1) })),
});
export type ConnectionCardBackDto = z.infer<typeof ConnectionCardBackDto>;

export const PublicProfileShowcaseClubDto = z.object({
  id: z.uuid(),
  slug: z.string().min(1),
  name: z.string().min(1),
  /** Presigned URL логотипа клуба; null — логотипа нет, рисуем букву названия. */
  logoUrl: z.string().nullable(),
});
export type PublicProfileShowcaseClubDto = z.infer<typeof PublicProfileShowcaseClubDto>;

export const PublicProfileShowcaseEventDto = z.object({
  id: z.uuid(),
  title: z.string().min(1),
  startsAt: z.iso.datetime(),
});
export type PublicProfileShowcaseEventDto = z.infer<typeof PublicProfileShowcaseEventDto>;

export const PublicProfileShowcaseServiceDto = z.object({
  id: z.uuid(),
  title: z.string().min(1),
  /** Presigned URL обложки; null — обложки нет, рисуем нейтральную плашку. */
  coverUrl: z.string().nullable(),
  /** Строка целиком из услуги: внутри и валюта, и «от». Форматировать нечего. */
  priceText: z.string().nullable(),
  format: PublicCardServiceFormat,
  /** Текст владельца. Нет поля или null — на карточке описания нет. */
  description: z.string().nullable().optional(),
});
export type PublicProfileShowcaseServiceDto = z.infer<typeof PublicProfileShowcaseServiceDto>;

/**
 * Витрина под кнопкой совместимости на GET /users/:id.
 * Колода, чат и модалка участника поле не шлют.
 */
export const PublicProfileShowcaseDto = z.object({
  clubs: z.array(PublicProfileShowcaseClubDto),
  services: z.array(PublicProfileShowcaseServiceDto),
  lookingFor: z.array(LookingForSlug),
  offering: z.array(OfferingSlug),
  headline: z.string().nullable(),
  superpower: z.string().nullable(),
  bioRole: z.string().nullable().optional(),
  bioSkills: z.string().nullable().optional(),
  bioIndustry: z.string().nullable().optional(),
  bioRequest: z.string().nullable().optional(),
  ...BusinessCardTagsDto.shape,
  spokenLanguages: z.array(Iso6391),
  links: z.array(PublicCardExternalLinkDto),
  socials: PublicCardSocialsDto,
  directions: z.array(PublicCardDirectionDto),
  /** «Моя деятельность» — about фасета business. Нет строки — поле можно не присылать. */
  activity: z.string().nullable().optional(),
  /**
   * Готовые подписи чипов «Навыки и темы» — собраны сервером из хэштегов своих
   * событий, интересов и категорий посещённых (`buildProfileTopics`). Не слаги:
   * хэштег и название категории переводить нечем, а смешивать в одном ряду
   * локализуемое с нелокализуемым — значит показать половину блока по-русски.
   */
  topics: z.array(z.string()),
  upcomingEvents: z.array(PublicProfileShowcaseEventDto),
  /**
   * Проведённые события, новыми вперёд. Портфолио организатора: у 143 из 162
   * организаторов прода нет ни одного будущего события, и витрина без этой
   * ленты утверждала, что человек ничего не делал.
   */
  pastEvents: z.array(PublicProfileShowcaseEventDto),
  upcomingRsvps: z.array(PublicProfileShowcaseEventDto),
});
export type PublicProfileShowcaseDto = z.infer<typeof PublicProfileShowcaseDto>;

export const EMPTY_PUBLIC_PROFILE_SHOWCASE: PublicProfileShowcaseDto = {
  clubs: [],
  services: [],
  lookingFor: [],
  offering: [],
  headline: null,
  superpower: null,
  bioRole: null,
  bioSkills: null,
  bioIndustry: null,
  bioRequest: null,
  ...EMPTY_BUSINESS_CARD_TAGS,
  spokenLanguages: [],
  links: [],
  socials: [],
  directions: [],
  topics: [],
  upcomingEvents: [],
  pastEvents: [],
  upcomingRsvps: [],
};

/** Платформенный ранг на лице Connection Card (Новичок…Легенда). */
export const PublicCardRankDto = z.object({
  level: z.number().int().min(1).max(8),
  name: z.string().min(1),
});
export type PublicCardRankDto = z.infer<typeof PublicCardRankDto>;

export const PublicCardSphereDto = z.object({
  name: z.string(),
  emoji: z.string(),
});
export type PublicCardSphereDto = z.infer<typeof PublicCardSphereDto>;

/**
 * Публичная визитка. НЕ переиспользует PublicProfileDto намеренно: тот несёт
 * age и сырые psychTests, которые анонимному гостю не показываем (спека 2026-08-31).
 * Код личности — публичная формула без внутренних методик.
 */
export const PublicCardDto = z.object({
  code: z.string(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  cityName: z.string().nullable(),
  /** IANA-таймзона города владельца — для форматирования дат upcomingEvents; null, если города нет. */
  timeZone: z.string().nullable(),
  bio: z.string().nullable(),
  /** Слаги интересов: их показывает оборот Connection Card, где ряд свой. */
  interests: z.array(InterestSlug),
  /** Готовые подписи чипов «Навыки и темы» — см. одноимённое поле витрины. */
  topics: z.array(z.string()),
  services: z.array(PublicCardServiceDto),
  /** Клубы во владении; пустой массив → секция печатает «незаполнено» (ADR-070). */
  clubs: z.array(PublicCardClubDto),
  upcomingEvents: z.array(PublicCardEventDto),
  /** Проведённые события, новыми вперёд — портфолио организатора. */
  pastEvents: z.array(PublicCardEventDto),
  stats: PublicCardStatsDto,
  visitedCount: z.number().int().nonnegative(),
  memberSince: z.iso.datetime(),
  alive: z.boolean(),
  spheres: z.array(PublicCardSphereDto),
  /**
   * Витрина — только то, что правит единый редактор профиля (2026-09-30).
   * Суперсила, «Кто я» текстом, заголовок, текстовые фолбэки навыков/индустрии/
   * запроса, языки, ссылки и «куда иду» в БД остались, но наружу не едут.
   * Соцсети снова едут: их правит редактор профиля.
   */
  ...BusinessCardTagsDto.shape,
  /** «Главный запрос», пока у человека нет requestTags. */
  lookingFor: z.array(LookingForSlug),
  offering: z.array(OfferingSlug),
  directions: z.array(PublicCardDirectionDto),
  /** Соцсети под пластиковой картой. Пустой список — ряда иконок нет. */
  socials: PublicCardSocialsDto,
  /** Свой логотип шапки. null — логотип UpMe. */
  logoUrl: z.string().nullable(),
  /** Тема пластика. Нет поля — «Наша». */
  theme: PublicCardTheme.default('ours'),
  /** «Моя деятельность» — about фасета business. Нет строки — поле можно не присылать. */
  activity: z.string().nullable().optional(),
  /** Общий ранг платформы по колесу сфер — как на Connection Card в приложении. */
  rank: PublicCardRankDto,
  /** Публичный Код личности; null, пока не собраны все три сигнала. */
  personalityCode: PublicPersonalityCodeDto.nullable().default(null),
  /**
   * VIP активен на момент рендера. Страница анонимная и краулерная, поэтому
   * наружу уходит только флаг — даты выдачи и истечения остаются внутренними.
   */
  isVip: z.boolean(),
});
export type PublicCardDto = z.infer<typeof PublicCardDto>;

/** Потолок списков услуг и событий на визитке. */
export const PUBLIC_CARD_MAX_ITEMS = 5;
